import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMOJI_LIST = [
  '😀','😁','😂','🤣','😊','😍','😘','😎','🤩','🥳','😇','🙂','😉','😌','😋',
  '🤔','🤨','😐','😑','😶','🙄','😏','😴','😪','🤤','😛','😜','😝','🤗','🤭',
  '👍','👎','👏','🙏','💪','✌️','🤝','👌','✅','❌','⭐','🔥','💯','🎉','🎊',
  '❤️','🧡','💛','💚','💙','💜','🤍','🖤','💔','💕','💖','💗','📩','📨','📬',
];

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('az-AZ', {
      month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = reader.result || '';
    const idx = String(result).indexOf(',');
    resolve(idx >= 0 ? String(result).slice(idx + 1) : String(result));
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'image', 'blockquote'],
    ['clean'],
  ],
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'align', 'link', 'image', 'blockquote',
];

const MailPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [folder, setFolder] = useState('inbox');
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [trash, setTrash] = useState({ inbox: [], sent: [] });
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [composeData, setComposeData] = useState({ to: '', subject: '', html: '' });
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [banner, setBanner] = useState(null); // { type, text }
  const fileInputRef = useRef(null);
  const quillRef = useRef(null);

  useEffect(() => {
    if (user && user.role !== 'super_admin') { navigate('/dashboard'); return; }
    refreshAll().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const refreshAll = () => Promise.all([fetchInbox(), fetchSent(), fetchTrash()]);

  const fetchInbox = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/contact`, { withCredentials: true });
      setInbox(data || []);
    } catch (err) { console.error('Inbox fetch:', err); }
  };

  const fetchSent = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/mail/sent`, { withCredentials: true });
      setSent(data || []);
    } catch (err) { console.error('Sent fetch:', err); }
  };

  const fetchTrash = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/mail/trash`, { withCredentials: true });
      setTrash(data || { inbox: [], sent: [] });
    } catch (err) { console.error('Trash fetch:', err); }
  };

  const clearSelection = () => setSelectedIds(new Set());
  const switchFolder = (id) => { setFolder(id); setSelected(null); clearSelection(); setBanner(null); };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const flashBanner = (type, text) => {
    setBanner({ type, text });
    setTimeout(() => setBanner(null), 3000);
  };

  const openCompose = (prefill = {}) => {
    setSelected(null);
    setFolder('compose');
    clearSelection();
    setComposeData({
      to: prefill.to || '',
      subject: prefill.subject || '',
      html: prefill.html || '',
    });
    setAttachments([]);
    setSendStatus(null);
    setShowEmoji(false);
  };

  const openReply = (msg) => {
    const originalText = (msg.message || '').replace(/\n/g, '<br/>');
    const quoted = `<br/><br/><blockquote style="border-left:3px solid #e7e5e4;padding-left:12px;color:#78716c;">
<strong>${msg.name || ''}</strong> &lt;${msg.email || ''}&gt; yazdı:<br/>${originalText}
</blockquote>`;
    openCompose({
      to: msg.email || '',
      subject: `Re: ${msg.subject || 'SmartMenyu.az müraciətiniz'}`,
      html: quoted,
    });
  };

  const handleFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const encoded = await Promise.all(files.map(async (f) => ({
      filename: f.name,
      size: f.size,
      content_base64: await fileToBase64(f),
    })));
    setAttachments((prev) => [...prev, ...encoded]);
    e.target.value = '';
  };

  const removeAttachment = (idx) => setAttachments((prev) => prev.filter((_, i) => i !== idx));

  const insertEmoji = (emoji) => {
    const editor = quillRef.current?.getEditor?.();
    if (editor) {
      const range = editor.getSelection(true);
      editor.insertText(range?.index ?? editor.getLength(), emoji, 'user');
      editor.setSelection((range?.index ?? editor.getLength()) + emoji.length, 0);
    } else {
      setComposeData((d) => ({ ...d, html: (d.html || '') + emoji }));
    }
    setShowEmoji(false);
  };

  const discardDraft = () => {
    if (composeData.html || composeData.to || composeData.subject || attachments.length) {
      if (!window.confirm('Qaralamanı ləğv etmək istədiyinizə əminsiniz?')) return;
    }
    setComposeData({ to: '', subject: '', html: '' });
    setAttachments([]);
    setSendStatus(null);
    setFolder('inbox');
  };

  const sendMail = async () => {
    setSendStatus(null);
    const plainText = composeData.html.replace(/<[^>]+>/g, '').trim();
    if (!composeData.to.trim() || !composeData.subject.trim() || !plainText) {
      setSendStatus({ type: 'error', text: 'Alıcı, mövzu və mətn məcburidir' });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/mail/send`, {
        to: composeData.to,
        subject: composeData.subject,
        body: plainText,
        html: composeData.html,
        attachments: attachments.map(({ filename, content_base64 }) => ({ filename, content_base64 })),
      }, { withCredentials: true });
      setSendStatus({ type: 'success', text: '✅ Mail uğurla göndərildi' });
      setComposeData({ to: '', subject: '', html: '' });
      setAttachments([]);
      await fetchSent();
      setTimeout(() => { setFolder('sent'); setSendStatus(null); }, 1200);
    } catch (err) {
      setSendStatus({ type: 'error', text: err.response?.data?.detail || 'Göndərilə bilmədi' });
    } finally {
      setSending(false);
    }
  };

  // ============ Bulk actions ============
  const bulkDelete = async () => {
    if (!selectedIds.size) return;
    const ids = Array.from(selectedIds);
    if (!window.confirm(`${ids.length} mesajı Silinənlər qovluğuna göndərmək istəyirsiniz?`)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/mail/delete`, { folder, ids }, { withCredentials: true });
      await refreshAll();
      clearSelection();
      setSelected(null);
      flashBanner('success', `🗑 ${ids.length} mesaj Silinənlər qovluğuna köçürüldü`);
    } catch (err) {
      flashBanner('error', err.response?.data?.detail || 'Silinə bilmədi');
    }
  };

  const deleteOne = async (id) => {
    if (!window.confirm('Bu mesajı Silinənlər qovluğuna göndərmək istəyirsiniz?')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/mail/delete`, { folder, ids: [id] }, { withCredentials: true });
      await refreshAll();
      if (selected?.id === id) setSelected(null);
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
      flashBanner('success', '🗑 Mesaj Silinənlər qovluğuna köçürüldü');
    } catch (err) {
      flashBanner('error', err.response?.data?.detail || 'Silinə bilmədi');
    }
  };

  const restoreFromTrash = async (subFolder, ids) => {
    if (!ids.length) return;
    try {
      await axios.post(`${BACKEND_URL}/api/mail/restore`, { folder: subFolder, ids }, { withCredentials: true });
      await refreshAll();
      clearSelection();
      setSelected(null);
      flashBanner('success', `♻️ ${ids.length} mesaj bərpa edildi`);
    } catch (err) {
      flashBanner('error', err.response?.data?.detail || 'Bərpa edilə bilmədi');
    }
  };

  const purgeItems = async (subFolder, ids) => {
    if (!ids.length) return;
    if (!window.confirm(`${ids.length} mesajı BİRDƏFƏLİK silmək istədiyinizə əminsiniz? Bu əməliyyat geri qaytarıla bilməz.`)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/mail/purge`, { folder: subFolder, ids }, { withCredentials: true });
      await refreshAll();
      clearSelection();
      setSelected(null);
      flashBanner('success', `🔥 ${ids.length} mesaj birdəfəlik silindi`);
    } catch (err) {
      flashBanner('error', err.response?.data?.detail || 'Silinə bilmədi');
    }
  };

  const emptyTrash = async () => {
    const total = (trash.inbox?.length || 0) + (trash.sent?.length || 0);
    if (!total) { flashBanner('error', 'Silinənlər qovluğu artıq boşdur'); return; }
    if (!window.confirm(`Silinənlər qovluğunda olan ${total} mesajı BİRDƏFƏLİK silmək istəyirsiniz? Bu əməliyyat geri qaytarıla bilməz.`)) return;
    try {
      await Promise.all([
        axios.post(`${BACKEND_URL}/api/mail/purge`, { folder: 'inbox', empty_all: true }, { withCredentials: true }),
        axios.post(`${BACKEND_URL}/api/mail/purge`, { folder: 'sent', empty_all: true }, { withCredentials: true }),
      ]);
      await refreshAll();
      clearSelection();
      setSelected(null);
      flashBanner('success', `🔥 Silinənlər qovluğu boşaldıldı (${total})`);
    } catch (err) {
      flashBanner('error', err.response?.data?.detail || 'Boşaldıla bilmədi');
    }
  };

  // ============ Current list ============
  const combinedTrash = useMemo(() => {
    const withKind = (arr, kind) => (arr || []).map((m) => ({ ...m, _kind: kind }));
    return [...withKind(trash.inbox, 'inbox'), ...withKind(trash.sent, 'sent')]
      .sort((a, b) => new Date(b.deleted_at || 0) - new Date(a.deleted_at || 0));
  }, [trash]);

  const currentList = folder === 'sent' ? sent : folder === 'trash' ? combinedTrash : inbox;

  const filtered = useMemo(() => currentList.filter((m) => {
    if (!search) return true;
    const q = search.toLowerCase();
    let target = '';
    if (folder === 'sent') target = `${m.to} ${m.subject} ${m.body || ''}`;
    else if (folder === 'trash') {
      target = m._kind === 'sent'
        ? `${m.to} ${m.subject} ${m.body || ''}`
        : `${m.name} ${m.email} ${m.message} ${m.restaurant_name || ''}`;
    } else target = `${m.name} ${m.email} ${m.message} ${m.restaurant_name || ''}`;
    return target.toLowerCase().includes(q);
  }), [currentList, search, folder]);

  const allSelected = filtered.length > 0 && filtered.every((m) => selectedIds.has(m.id));
  const toggleSelectAll = () => {
    if (allSelected) clearSelection();
    else setSelectedIds(new Set(filtered.map((m) => m.id)));
  };

  const trashTotal = (trash.inbox?.length || 0) + (trash.sent?.length || 0);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-stone-50"><div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div></div>;

  const isTrash = folder === 'trash';
  const isCompose = folder === 'compose';

  return (
    <div className="min-h-screen bg-stone-50" data-testid="mail-panel">
      <div className="bg-white border-b border-stone-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate('/dashboard')} className="text-stone-600 hover:text-emerald-600 flex items-center gap-1" data-testid="mail-back-btn">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
              <span className="hidden sm:inline">Panel</span>
            </button>
            <div className="text-lg sm:text-xl font-bold text-stone-900">📬 SmartMenyu Mail</div>
          </div>
          <div className="text-xs text-stone-500 truncate">{user?.email}</div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4">
        {banner && (
          <div className={`mb-3 px-4 py-2.5 rounded-lg text-sm ${banner.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="mail-banner">
            {banner.text}
          </div>
        )}
        <div className="grid grid-cols-12 gap-4">
          {/* Sidebar */}
          <div className="col-span-12 md:col-span-3 space-y-1">
            <button
              onClick={() => openCompose()}
              className="w-full px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold shadow-sm flex items-center justify-center gap-2 mb-3"
              data-testid="mail-compose-btn"
            >
              ✏️ Yaz
            </button>
            {[
              { id: 'inbox', label: 'Gələn qutusu', icon: '📥', count: inbox.length },
              { id: 'sent', label: 'Göndərilən', icon: '📤', count: sent.length },
              { id: 'trash', label: 'Silinənlər', icon: '🗑', count: trashTotal },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => switchFolder(f.id)}
                className={`w-full px-3 py-2.5 rounded-lg text-left flex items-center justify-between transition ${folder === f.id ? 'bg-emerald-100 text-emerald-800 font-semibold' : 'text-stone-700 hover:bg-stone-100'}`}
                data-testid={`mail-folder-${f.id}`}
              >
                <span>{f.icon} {f.label}</span>
                <span className="text-xs opacity-60">{f.count}</span>
              </button>
            ))}
            {isTrash && trashTotal > 0 && (
              <button
                onClick={emptyTrash}
                className="w-full mt-3 px-3 py-2 text-xs text-red-600 hover:bg-red-50 border border-red-200 rounded-lg font-medium"
                data-testid="mail-empty-trash-btn"
              >
                🔥 Silinənləri boşalt ({trashTotal})
              </button>
            )}
          </div>

          {/* Main pane */}
          <div className="col-span-12 md:col-span-9">
            {isCompose ? (
              <div className="bg-white rounded-xl border border-stone-200 p-5" data-testid="mail-compose-form">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-stone-900">Yeni mail</h2>
                  <button onClick={discardDraft} className="text-stone-400 hover:text-red-600 p-1" title="Ləğv et və sil" data-testid="mail-discard-btn">🗑</button>
                </div>

                <div className="space-y-3">
                  <input type="email" placeholder="Kimə (email)" value={composeData.to} onChange={(e) => setComposeData({ ...composeData, to: e.target.value })} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent" data-testid="mail-to-input" />
                  <input type="text" placeholder="Mövzu" value={composeData.subject} onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent" data-testid="mail-subject-input" />

                  <div className="border border-stone-300 rounded-lg overflow-hidden" data-testid="mail-body-editor">
                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      value={composeData.html}
                      onChange={(html) => setComposeData((d) => ({ ...d, html }))}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Mesajı yazın..."
                      style={{ minHeight: '260px' }}
                    />
                  </div>

                  {attachments.length > 0 && (
                    <div className="space-y-1.5" data-testid="mail-attachments">
                      {attachments.map((a, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm">
                          <span className="truncate flex items-center gap-2">📎 <span className="truncate">{a.filename}</span> <span className="text-xs text-stone-500">({(a.size / 1024).toFixed(1)} KB)</span></span>
                          <button onClick={() => removeAttachment(i)} className="text-red-500 hover:text-red-700 text-lg leading-none" data-testid={`mail-remove-attachment-${i}`}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {sendStatus && (
                  <div className={`mt-3 px-3 py-2 rounded-lg text-sm ${sendStatus.type === 'success' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`} data-testid="mail-send-status">
                    {sendStatus.text}
                  </div>
                )}

                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <button onClick={sendMail} disabled={sending} className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg font-semibold flex items-center gap-2" data-testid="mail-send-btn">
                    {sending ? 'Göndərilir...' : (<>📮 Göndər</>)}
                  </button>

                  <input ref={fileInputRef} type="file" multiple hidden onChange={handleFilePick} data-testid="mail-file-input" />
                  <button onClick={() => fileInputRef.current?.click()} className="w-9 h-9 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-600 text-lg" title="Fayl əlavə et" data-testid="mail-attach-btn">📎</button>

                  <div className="relative">
                    <button onClick={() => setShowEmoji((s) => !s)} className="w-9 h-9 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-600 text-lg" title="Emoji əlavə et" data-testid="mail-emoji-btn">😊</button>
                    {showEmoji && (
                      <div className="absolute bottom-11 left-0 z-30 bg-white border border-stone-200 rounded-xl shadow-xl p-3 w-72 grid grid-cols-8 gap-1" data-testid="mail-emoji-picker">
                        {EMOJI_LIST.map((e) => (
                          <button key={e} onClick={() => insertEmoji(e)} className="w-8 h-8 hover:bg-stone-100 rounded text-lg">{e}</button>
                        ))}
                      </div>
                    )}
                  </div>

                  <button onClick={() => {
                    const url = window.prompt('Link URL-i:');
                    if (!url) return;
                    const text = window.prompt('Link mətni:', url) || url;
                    const editor = quillRef.current?.getEditor?.();
                    if (editor) {
                      const range = editor.getSelection(true);
                      editor.insertText(range?.index ?? editor.getLength(), text, { link: url }, 'user');
                    }
                  }} className="w-9 h-9 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-600 text-lg" title="Link əlavə et" data-testid="mail-link-btn">🔗</button>

                  <button onClick={() => {
                    const url = window.prompt('Şəkil URL-i:');
                    if (!url) return;
                    const editor = quillRef.current?.getEditor?.();
                    if (editor) {
                      const range = editor.getSelection(true);
                      editor.insertEmbed(range?.index ?? editor.getLength(), 'image', url, 'user');
                    }
                  }} className="w-9 h-9 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-600 text-lg" title="Şəkil URL" data-testid="mail-image-btn">🖼</button>

                  <div className="flex-1"></div>

                  <button onClick={() => setFolder('inbox')} className="px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg font-medium" data-testid="mail-cancel-btn">Ləğv et</button>
                </div>
              </div>
            ) : (
              <>
                {/* List toolbar */}
                <div className="mb-3 flex items-center gap-2 flex-wrap">
                  {filtered.length > 0 && (
                    <label className="flex items-center gap-2 text-xs text-stone-600 cursor-pointer px-2 py-1 rounded hover:bg-stone-100" data-testid="mail-select-all">
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="w-4 h-4 accent-emerald-600" />
                      <span>{allSelected ? 'Seçimi ləğv et' : 'Hamısını seç'}</span>
                    </label>
                  )}
                  {selectedIds.size > 0 && !isTrash && (
                    <button onClick={bulkDelete} className="text-xs px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg border border-red-200 font-medium flex items-center gap-1" data-testid="mail-bulk-delete-btn">
                      🗑 Sil ({selectedIds.size})
                    </button>
                  )}
                  {selectedIds.size > 0 && isTrash && (
                    <>
                      <button onClick={() => {
                        const inboxIds = filtered.filter((m) => selectedIds.has(m.id) && m._kind === 'inbox').map((m) => m.id);
                        const sentIds = filtered.filter((m) => selectedIds.has(m.id) && m._kind === 'sent').map((m) => m.id);
                        (async () => {
                          if (inboxIds.length) await restoreFromTrash('inbox', inboxIds);
                          if (sentIds.length) await restoreFromTrash('sent', sentIds);
                        })();
                      }} className="text-xs px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg border border-emerald-200 font-medium" data-testid="mail-bulk-restore-btn">
                        ♻️ Bərpa et ({selectedIds.size})
                      </button>
                      <button onClick={() => {
                        const inboxIds = filtered.filter((m) => selectedIds.has(m.id) && m._kind === 'inbox').map((m) => m.id);
                        const sentIds = filtered.filter((m) => selectedIds.has(m.id) && m._kind === 'sent').map((m) => m.id);
                        (async () => {
                          if (inboxIds.length) await purgeItems('inbox', inboxIds);
                          if (sentIds.length) await purgeItems('sent', sentIds);
                        })();
                      }} className="text-xs px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg border border-red-200 font-medium" data-testid="mail-bulk-purge-btn">
                        🔥 Birdəfəlik sil
                      </button>
                    </>
                  )}
                  <input type="search" placeholder={`${isTrash ? 'Silinənlər' : folder === 'inbox' ? 'Gələnlər' : 'Göndərilənlər'}-də axtar...`} value={search} onChange={(e) => setSearch(e.target.value)} className="ml-auto flex-1 min-w-[180px] max-w-xs px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="mail-search-input" />
                </div>

                <div className="grid lg:grid-cols-5 gap-3">
                  <div className="lg:col-span-2 space-y-1.5 lg:max-h-[65vh] lg:overflow-y-auto">
                    {filtered.length === 0 ? (
                      <div className="text-center py-10 text-stone-500 text-sm">{search ? 'Nəticə yoxdur' : (isTrash ? 'Silinənlər qovluğu boşdur' : 'Heç bir mesaj yoxdur')}</div>
                    ) : (
                      filtered.map((m) => {
                        const isSent = folder === 'sent' || (isTrash && m._kind === 'sent');
                        return (
                          <div
                            key={m.id}
                            className={`group relative rounded-lg border transition ${selected?.id === m.id ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-stone-200 hover:border-emerald-200'} ${selectedIds.has(m.id) ? 'ring-2 ring-emerald-400' : ''}`}
                          >
                            <div className="flex items-start gap-2 p-3">
                              <input
                                type="checkbox"
                                checked={selectedIds.has(m.id)}
                                onChange={(e) => { e.stopPropagation(); toggleSelect(m.id); }}
                                className="mt-1 w-4 h-4 accent-emerald-600 cursor-pointer"
                                data-testid={`mail-select-${m.id}`}
                              />
                              <button
                                onClick={() => setSelected(m)}
                                className="flex-1 text-left min-w-0"
                                data-testid={`mail-item-${m.id}`}
                              >
                                <div className="flex justify-between gap-2 mb-1">
                                  <span className="font-semibold text-sm truncate">
                                    {isTrash && (<span className="text-[10px] uppercase mr-1 px-1.5 py-0.5 rounded bg-stone-200 text-stone-700">{m._kind === 'sent' ? 'Göndərilən' : 'Gələn'}</span>)}
                                    {isSent ? m.to : (m.name || m.email)}
                                  </span>
                                  <span className="text-[10px] text-stone-500 shrink-0">{formatDate(isTrash ? m.deleted_at : m.created_at)}</span>
                                </div>
                                <div className="text-xs text-stone-600 truncate">{isSent ? m.subject : m.email}</div>
                                <div className="text-xs text-stone-500 mt-1 line-clamp-2">{isSent ? (m.body || '') : m.message}</div>
                              </button>
                              {!isTrash && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); deleteOne(m.id); }}
                                  className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-600 transition p-1"
                                  title="Sil"
                                  data-testid={`mail-delete-${m.id}`}
                                >🗑</button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div className="lg:col-span-3">
                    {selected ? (
                      <div className="bg-white rounded-xl border border-stone-200 p-5" data-testid="mail-detail">
                        <div className="flex justify-between gap-3 pb-3 border-b border-stone-100 mb-3">
                          <div className="min-w-0">
                            {(() => {
                              const isSentDetail = folder === 'sent' || (isTrash && selected._kind === 'sent');
                              return (
                                <>
                                  <h2 className="text-lg font-bold text-stone-900">{isSentDetail ? selected.subject : selected.name}</h2>
                                  <div className="text-sm text-stone-600 break-all">{isSentDetail ? `→ ${selected.to}` : selected.email}</div>
                                  {!isSentDetail && selected.restaurant_name && <div className="text-sm text-emerald-700 mt-0.5">🍽 {selected.restaurant_name}</div>}
                                  {!isSentDetail && selected.phone && <div className="text-sm text-stone-600 mt-0.5">📞 {selected.phone}</div>}
                                </>
                              );
                            })()}
                          </div>
                          <div className="text-xs text-stone-500 shrink-0">{formatDate(selected.created_at)}</div>
                        </div>

                        {((folder === 'sent' && selected.html) || (isTrash && selected._kind === 'sent' && selected.html)) ? (
                          <div className="prose prose-sm max-w-none text-stone-800" dangerouslySetInnerHTML={{ __html: selected.html }} />
                        ) : (
                          <div className="whitespace-pre-wrap text-stone-800 leading-relaxed text-sm">
                            {(folder === 'sent' || (isTrash && selected._kind === 'sent')) ? selected.body : selected.message}
                          </div>
                        )}

                        {((folder === 'sent' && selected.attachments?.length > 0) || (isTrash && selected._kind === 'sent' && selected.attachments?.length > 0)) && (
                          <div className="mt-4 pt-3 border-t border-stone-100">
                            <div className="text-xs text-stone-500 mb-1.5">Əlavələr:</div>
                            <div className="flex flex-wrap gap-2">
                              {selected.attachments.map((a, i) => (
                                <div key={i} className="text-xs bg-stone-100 px-2 py-1 rounded">📎 {a.filename}</div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="mt-4 flex flex-wrap gap-2">
                          {folder === 'inbox' && (
                            <button onClick={() => openReply(selected)} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg font-semibold" data-testid="mail-reply-btn">
                              ↩️ Cavab yaz
                            </button>
                          )}
                          {!isTrash && (
                            <button onClick={() => deleteOne(selected.id)} className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 text-sm rounded-lg font-medium border border-red-200" data-testid="mail-detail-delete-btn">
                              🗑 Sil
                            </button>
                          )}
                          {isTrash && (
                            <>
                              <button onClick={() => restoreFromTrash(selected._kind, [selected.id])} className="px-4 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm rounded-lg font-medium border border-emerald-200" data-testid="mail-detail-restore-btn">
                                ♻️ Bərpa et
                              </button>
                              <button onClick={() => purgeItems(selected._kind, [selected.id])} className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 text-sm rounded-lg font-medium border border-red-200" data-testid="mail-detail-purge-btn">
                                🔥 Birdəfəlik sil
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white rounded-xl border border-stone-200 p-12 text-center text-stone-500 text-sm">Sol tərəfdən mesaj seçin</div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MailPanel;
